import React, { useState } from "react";
import Styles from "./SourceDestination.module.scss";
import classnames from "classnames";

//Components
import Typography from "@vahak/core-ui/dist/components/Typography";
import GoogleMapIcon from "@vahak/core/dist/icons/google-map-icon.svg";
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";

//Assets
import SourceIcon from "@vahak/core/dist/standard-icons/source.svg";
import DestinationIcon from "@vahak/core/dist/standard-icons/destination.svg";
// Constants
import COLORS from "@vahak/core-ui/dist/constants/colors";
import FontSize from "@vahak/core-ui/dist/constants/fontSize";
import Chip from "@vahak/core-ui/dist/components/Chip";
import { LatLng, openGoogleMapsRoute } from "../../../../methods/maps";
import TextCTA from "@vahak/core-ui/dist/components/TextCTA";
import StopPropagation from "../../../helper/StopPropagation";
import Flex, { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";

interface SourceDestinationProps {
    id?: string;
    source?: string;
    destination?: string;
    sourceCity?: string;
    sourceState?: string;
    destCity?: string;
    destState?: string;
    distanceInKm?: number;
    distanceInMtr?: number;
    fontSize?: keyof Pick<typeof FontSize, "sm" | "s" | "xs">;
    ellipses?: boolean;
    srcLatLng?: LatLng;
    destLatLng?: LatLng;
    route?: LatLng[];
}

const SourceDestination = ({
    id,
    destination,
    source,
    destCity,
    destState,
    sourceCity,
    sourceState,
    distanceInKm,
    distanceInMtr = 0,
    fontSize = "sm",
    ellipses = false,
    srcLatLng,
    destLatLng,
    route
}: SourceDestinationProps) => {
    // const src = source || (sourceCity || sourceState ? [sourceCity, sourceState].filter((v) => !!v).join(", ") : " ");
    // const dest = destination || (destCity || destState ? [destCity, destState].filter((v) => !!v).join(", ") : " ");
    const distanceInKmCalculatedFromMtr = Math.ceil(distanceInMtr / 1000);
    const showDistance = !!distanceInKm || !!distanceInKmCalculatedFromMtr;
    const [isSrcExpanded, setIsSrcExpanded] = useState(false);
    const [isDestExpanded, setIsDestExpanded] = useState(false);

    const toggleExpand = (type: "src" | "dest") => {
        type === "src" && setIsSrcExpanded((v) => !v);
        type === "dest" && setIsDestExpanded((v) => !v);
    };

    return (
        <div className={Styles.main}>
            <div className={Styles.locations}>
                <div
                    className={Styles.location}
                    {...(!!srcLatLng && {
                        onClick: () => openGoogleMapsRoute([srcLatLng]),
                        style: {
                            cursor: "pointer"
                        }
                    })}
                >
                    <div className={Styles.icon}>
                        <SourceIcon />
                    </div>

                    <div className={Styles.textWrapper}>
                        <div
                            className={classnames(Styles.info, { [Styles.ellipses]: ellipses && !isSrcExpanded })}
                            id={`${id}--source`}
                        >
                            <Typography weight="medium" size={fontSize} color={COLORS.GREY_900}>
                                {source || sourceCity}
                                {!!sourceState && !source && (
                                    <>
                                        {`, `}
                                        <Typography weight="medium" size={fontSize} color={COLORS.GREY_500}>
                                            {sourceState}
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        </div>
                        {ellipses && !isSrcExpanded && (
                            <StopPropagation>
                                <TextCTA
                                    weight="medium"
                                    size="s"
                                    underline
                                    onClick={toggleExpand.bind(undefined, "src")}
                                >
                                    more
                                </TextCTA>
                            </StopPropagation>
                        )}
                    </div>

                    {!!srcLatLng && (
                        <IconWrapper width={20} height={20}>
                            <GoogleMapIcon />
                        </IconWrapper>
                    )}
                </div>
                <div
                    className={Styles.location}
                    id={`${id}--destination`}
                    {...(!!destLatLng && {
                        onClick: () => openGoogleMapsRoute([destLatLng]),
                        style: {
                            cursor: "pointer"
                        }
                    })}
                >
                    <div className={Styles.icon}>
                        <DestinationIcon />
                    </div>
                    <div className={Styles.textWrapper}>
                        <div className={classnames(Styles.info, ellipses && !isDestExpanded ? Styles.ellipses : "")}>
                            <Typography weight="medium" size={fontSize} color={COLORS.GREY_900}>
                                {destination || destCity}
                                {!!destState && !destination && (
                                    <>
                                        {`, `}
                                        <Typography weight="medium" size={fontSize} color={COLORS.GREY_500}>
                                            {destState}
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        </div>
                        {ellipses && !isDestExpanded && (
                            <StopPropagation>
                                <TextCTA
                                    weight="medium"
                                    size="s"
                                    underline
                                    onClick={toggleExpand.bind(undefined, "dest")}
                                >
                                    more
                                </TextCTA>
                            </StopPropagation>
                        )}
                    </div>
                    {!!destLatLng && (
                        <IconWrapper width={20} height={20}>
                            <GoogleMapIcon />
                        </IconWrapper>
                    )}
                </div>
            </div>
            <VerticalFlex alignItems="center" gap={12}>
                {showDistance ? (
                    <Chip weight="medium" size="auto" color={"grey"} filled id={`${id}--kilometer`} radius={50}>
                        {distanceInKm || distanceInKmCalculatedFromMtr} km
                    </Chip>
                ) : undefined}
                {route?.length ? (
                    <Chip
                        outlined
                        color="grey"
                        radius={60}
                        onClick={() => {
                            openGoogleMapsRoute(route);
                        }}
                        size="auto"
                    >
                        <Flex gap={4}>
                            <IconWrapper width={12} height={12}>
                                <GoogleMapIcon />
                            </IconWrapper>
                            Route
                        </Flex>
                    </Chip>
                ) : undefined}
            </VerticalFlex>
        </div>
    );
};

export default SourceDestination;
